import { useState } from "react";
import { FieldNameSchema, KvKindSchema, isConnectKind, kvValueProblem, type InventoryResponse, type KvKind, type ProfileField } from "@starsach/shared-types";
import { Hint } from "../../../components/Hint";
import { Card, DataTable, EmptyState, btnPrimary, inputClass } from "../../../components/ui";
import { TabMenu, type CsvShape } from "./TabMenu";

type Kv = Extract<ProfileField, { type: "kv" }>;

/** What a customer reads for each kind; the wire value is the key. */
export const KV_KIND_LABELS: Record<KvKind, string> = {
  text: "Text",
  number: "Number",
  boolean: "True / false",
  queue: "Queue",
  flow: "Contact flow",
  prompt: "Prompt",
  hours_of_operation: "Hours of operation",
  lex_bot: "Lex bot",
};

const kindOf = (k: Pick<Kv, "kind">): KvKind => k.kind ?? "text";

/** The problem with a value of a kind, including a Connect name the cache does not have - the same rule the form, the table and the CSV import apply. */
function problemWith(kind: KvKind, value: string, inventory: InventoryResponse | undefined): string | undefined {
  const problem = kvValueProblem(kind, value);
  if (problem || !isConnectKind(kind)) return problem;
  return inventory && !inventory.items.some((i) => i.type === kind && i.name === value) ? `No ${KV_KIND_LABELS[kind].toLowerCase()} called "${value}" in the Connect cache.` : undefined;
}

const shapeFor = (inventory: InventoryResponse | undefined): CsvShape<Kv> => ({
  name: "kv-pairs",
  headers: ["name", "kind", "value"],
  optional: ["kind"],
  example: [
    { type: "kv", name: "callback_number", value: "+18005550100" },
    { type: "kv", name: "max_queue_wait", kind: "number", value: "300" },
    { type: "kv", name: "offer_callback", kind: "boolean", value: "true" },
    { type: "kv", name: "overflow_queue", kind: "queue", value: inventory?.items.find((i) => i.type === "queue")?.name ?? "Tier 1 Queue" },
    { type: "kv", name: "after_hours_flow", kind: "flow", value: inventory?.items.find((i) => i.type === "flow")?.name ?? "After Hours" },
    { type: "kv", name: "hold_prompt", kind: "prompt", value: inventory?.items.find((i) => i.type === "prompt")?.name ?? "Hold music" },
    { type: "kv", name: "sales_hours", kind: "hours_of_operation", value: inventory?.items.find((i) => i.type === "hours_of_operation")?.name ?? "Office Hours" },
    { type: "kv", name: "triage_bot", kind: "lex_bot", value: inventory?.items.find((i) => i.type === "lex_bot")?.name ?? "Triage" },
  ],
  toRow: (k) => ({ name: k.name, kind: kindOf(k), value: k.value }),
  fromRow: (r, line) => {
    const name = (r["name"] ?? "").trim();
    if (!FieldNameSchema.safeParse(name).success) return `Line ${line}: "${name}" is not a name a flow can read - lower-case letters, digits and underscores.`;
    const rawKind = (r["kind"] ?? "").trim().toLowerCase().replace(/[\s-]+/g, "_") || "text";
    const parsedKind = KvKindSchema.safeParse(rawKind);
    if (!parsedKind.success) return `Line ${line}: "${r["kind"]}" is not a kind - one of ${KvKindSchema.options.join(", ")}.`;
    const kind = parsedKind.data;
    const value = kind === "boolean" ? (r["value"] ?? "").trim().toLowerCase() : (r["value"] ?? "");
    const problem = problemWith(kind, value, inventory);
    if (problem) return `Line ${line}: ${problem}`;
    return { type: "kv", name, ...(kind === "text" ? {} : { kind }), value };
  },
});

/**
 * Key-value pairs: a name the flow reads and the value it gets -
 * `$.External.kv.<name>`. Each carries a kind (#265): text, a number,
 * true/false, or a Connect object chosen by name from the cache, which
 * reaches the flow as its ARN. Edited in place; imported and exported as CSV.
 */
export function KvTab({ fields, draft, inventory, onUpdate, onAdd, onRemove, onReplaceAll, can = { create: true, delete: true } }: { fields: Kv[]; draft: ProfileField[]; inventory: InventoryResponse | undefined; onUpdate: (field: ProfileField, next: ProfileField) => void; onAdd: (field: ProfileField) => void; onRemove: (field: ProfileField) => void; onReplaceAll: (fields: Kv[]) => void; /** ADR-0017: the create and delete verbs on key-value pairs. */ can?: { create: boolean; delete: boolean } }) {
  const [name, setName] = useState("");
  const [kind, setKind] = useState<KvKind>("text");
  const [value, setValue] = useState("");
  const [problem, setProblem] = useState<string>();
  const shape = shapeFor(inventory);
  const withKind = (k: Kv, next: KvKind): Kv => {
    // A value that is still its new kind is kept; anything else starts over so the row never holds a value it refuses.
    const value = kvValueProblem(next, k.value) ? "" : k.value;
    const { kind: _drop, ...rest } = k;
    void _drop;
    return { ...rest, ...(next === "text" ? {} : { kind: next }), value };
  };
  return (
    <Card title="Key-value pairs">
      <form
        className="mb-4 flex flex-wrap items-end gap-3 rounded border border-dashed border-neutral-300 p-3 dark:border-neutral-700"
        aria-label="Add a key-value pair"
        onSubmit={(e) => {
          e.preventDefault();
          const n = name.trim();
          if (!FieldNameSchema.safeParse(n).success) return setProblem("A name is lower-case letters, digits and underscores, starting with a letter or digit: callback_number, max_queue_wait.");
          if (draft.some((f) => f.type === "kv" && f.name === n)) return setProblem(`There is already a pair called ${n}.`);
          const v = kind === "boolean" ? value.trim().toLowerCase() : value;
          const bad = problemWith(kind, v, inventory);
          if (bad) return setProblem(bad);
          onAdd({ type: "kv", name: n, ...(kind === "text" ? {} : { kind }), value: v });
          setName("");
          setValue("");
          setProblem(undefined);
        }}
      >
        <label className="text-xs">
          Name
          <input className={`${inputClass} w-56`} value={name} placeholder="callback_number" aria-label="New pair name" onChange={(e) => { setName(e.target.value); setProblem(undefined); }} />
        </label>
        {/* The controls carry their own accessible names; the words beside them are for the eye. */}
        <span className="block text-xs">
          <span className="flex items-center"><span aria-hidden="true">Kind</span><Hint id="flow.kv.kind" label="what a kind is" /></span>
          <KindSelect value={kind} label="New pair kind" onChange={(k) => { setKind(k); setValue(""); setProblem(undefined); }} />
        </span>
        <span className="block flex-1 text-xs">
          <span aria-hidden="true">Value</span>
          <ValueInput kind={kind} value={value} label="New pair value" inventory={inventory} onChange={(v) => { setValue(v); setProblem(undefined); }} />
        </span>
        <button type="submit" className={btnPrimary} disabled={!can.create}>Add pair</button>
        <span className="ml-auto">
          <TabMenu
            what="Key-value pairs"
            shape={shape}
            items={fields}
            canImport={can.create}
            canDeleteAll={can.delete}
            onImport={(items, replace) => {
              const byName = new Map((replace ? [] : fields).map((k) => [k.name, k]));
              for (const k of items) byName.set(k.name, k);
              onReplaceAll([...byName.values()]);
            }}
            onDeleteAll={() => onReplaceAll([])}
          />
        </span>
        {problem && <p role="alert" className="basis-full text-xs text-red-700 dark:text-red-300">{problem}</p>}
      </form>
      <DataTable
        caption="Key-value pairs in this profile"
        rows={fields}
        rowKey={(k) => k.name}
        sortBy={(k) => k.name}
        filter={fields.length > 5}
        empty={<EmptyState title="No pairs yet" hint="A pair is a name the flow reads and the value it gets: $.External.kv.<name>. Add one above, or import a CSV." />}
        columns={[
          { key: "name", header: "Name", render: (k) => <code className="text-xs">{k.name}</code> },
          { key: "kind", header: "Kind", render: (k) => <KindSelect value={kindOf(k)} label={`Kind of ${k.name}`} onChange={(next) => onUpdate(k, withKind(k, next))} /> },
          {
            key: "value",
            header: "Value",
            render: (k) => {
              const bad = problemWith(kindOf(k), k.value, inventory);
              return (
                <span className="block">
                  <ValueInput kind={kindOf(k)} value={k.value} label={`Value of ${k.name}`} inventory={inventory} describedBy={bad ? `kv-${k.name}-problem` : undefined} onChange={(value) => onUpdate(k, { ...k, value })} />
                  {bad && <span id={`kv-${k.name}-problem`} role="alert" className="mt-1 block text-xs text-red-700 dark:text-red-300">{bad}</span>}
                </span>
              );
            },
          },
          { key: "remove", header: "", render: (k) => (can.delete ? <button type="button" className="text-xs text-red-700 underline dark:text-red-300" onClick={() => onRemove(k)} aria-label={`Remove ${k.name}`}>Remove</button> : null) },
        ]}
      />
      <p className="mt-3 text-xs text-neutral-500">The flow reads a pair by name - <code>$.External.kv.callback_number</code> - so order never matters. A number or true/false arrives as such; a Connect object arrives as its ARN, with <code>$.External.kv.&lt;name&gt;_name</code> holding its name.</p>
    </Card>
  );
}

function KindSelect({ value, label, onChange }: { value: KvKind; label: string; onChange: (kind: KvKind) => void }) {
  return (
    <select className={`${inputClass} mt-0 w-auto`} value={value} aria-label={label} onChange={(e) => onChange(KvKindSchema.parse(e.target.value))}>
      {KvKindSchema.options.map((k) => <option key={k} value={k}>{KV_KIND_LABELS[k]}</option>)}
    </select>
  );
}

/** The control a value of this kind is entered with: text, a number, true/false, or a name from the cache. */
function ValueInput({ kind, value, label, inventory, describedBy, onChange }: { kind: KvKind; value: string; label: string; inventory: InventoryResponse | undefined; describedBy?: string | undefined; onChange: (value: string) => void }) {
  if (kind === "boolean") {
    return (
      <select className={`${inputClass} mt-0 w-auto`} value={value} aria-label={label} aria-describedby={describedBy} onChange={(e) => onChange(e.target.value)}>
        <option value="">Choose…</option>
        <option value="true">true</option>
        <option value="false">false</option>
      </select>
    );
  }
  if (isConnectKind(kind)) {
    const items = (inventory?.items ?? []).filter((i) => i.type === kind);
    return (
      <select className={`${inputClass} mt-0`} value={value} aria-label={label} aria-describedby={describedBy} onChange={(e) => onChange(e.target.value)}>
        <option value="">{items.length === 0 ? `No ${KV_KIND_LABELS[kind].toLowerCase()} in the Connect cache` : "Choose…"}</option>
        {/* Real names from this instance: the customer never sees or types an ARN. */}
        {items.map((i) => <option key={i.name} value={i.name}>{i.name}</option>)}
        {value && !items.some((i) => i.name === value) && <option value={value}>{value} (not in the cache)</option>}
      </select>
    );
  }
  // A text box even for a number: a number input swallows "asdasd" silently, and the point is to say why it is refused.
  return <input className={`${inputClass} mt-0`} inputMode={kind === "number" ? "decimal" : undefined} value={value} placeholder={kind === "number" ? "300" : "+18005550100"} aria-label={label} aria-describedby={describedBy} onChange={(e) => onChange(e.target.value)} />;
}
